import React, { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { authFetch } from '../utils/apiHelper';
import { API_URL } from '../config';
import { modalSecondaryBtn, modalPrimaryBtn, MODAL_BTN_ROW, modalCard, modalTitle, modalBody } from '../modalButtons';
import ModalOverlay from './ModalOverlay';

const fmtMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

// Abortable delay — rejects with AbortError the moment `signal` fires so a
// retry backoff doesn't keep the whole download alive after Cancel.
const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal && signal.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const t = setTimeout(() => {
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
  });

// Model assets (.splat/.sog/point clouds) run to hundreds of MB, so there is
// no fixed per-request deadline — instead the response body is streamed and a
// stall timer is reset on every chunk. A download that keeps making progress
// runs as long as it needs; only a genuinely stalled connection (no bytes for
// `stallMs`) is aborted and retried. Presigned S3 GETs, no cache-busting on
// retry — an extra query param would break the SigV4 signature.
async function fetchAsset(url, { signal, stallMs = 60000, retries = 3, baseDelay = 1000, onBytes } = {}) {
  let attempt = 0;
  while (true) {
    if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const ctrl = new AbortController();
    const onOuterAbort = () => ctrl.abort();
    if (signal) signal.addEventListener('abort', onOuterAbort, { once: true });
    let stallTimer;
    const armStall = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => ctrl.abort(), stallMs);
    };
    try {
      armStall();
      const resp = await fetch(url, { signal: ctrl.signal, cache: 'no-store' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      if (!resp.body || typeof resp.body.getReader !== 'function') {
        const blob = await resp.blob();
        if (onBytes) onBytes(blob.size);
        return blob;
      }

      const reader = resp.body.getReader();
      const chunks = [];
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        if (onBytes) onBytes(value.byteLength);
        armStall();
      }
      return new Blob(chunks, { type: resp.headers.get('Content-Type') || 'application/octet-stream' });
    } catch (err) {
      if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
      attempt += 1;
      if (attempt > retries) throw err;
      // eslint-disable-next-line no-await-in-loop
      await sleep(baseDelay * 2 ** (attempt - 1), signal);
    } finally {
      clearTimeout(stallTimer);
      if (signal) signal.removeEventListener('abort', onOuterAbort);
    }
  }
}

async function runPool(tasks, concurrency, signal) {
  let i = 0;
  await Promise.all(
    Array.from({ length: concurrency }, async () => {
      while (i < tasks.length) {
        if (signal && signal.aborted) return;
        // eslint-disable-next-line no-await-in-loop
        await tasks[i++]();
      }
    })
  );
}

// model: { modelId, name? }
const DownloadModal = ({ model, onClose }) => {
  const [working, setWorking] = useState(false);
  const [building, setBuilding] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [downloaded, setDownloaded] = useState(0);
  const [error, setError] = useState('');
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const bytesRef = useRef(0);
  const lastPaintRef = useRef(0);

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  const safeSet = (fn) => {
    if (mountedRef.current) fn();
  };

  const label = model.name || model.modelId;
  const safeName = String(label).replace(/[^\w.-]+/g, '_') || 'model';

  const handleDownload = async () => {
    const ac = new AbortController();
    abortRef.current = ac;
    const { signal } = ac;

    bytesRef.current = 0;
    lastPaintRef.current = 0;
    setWorking(true);
    setBuilding(false);
    setError('');
    setDownloaded(0);
    setProgress({ done: 0, total: 0 });

    const bumpBytes = (n) => {
      bytesRef.current += n;
      const now = Date.now();
      if (now - lastPaintRef.current > 200) {
        lastPaintRef.current = now;
        safeSet(() => setDownloaded(bytesRef.current));
      }
    };

    try {
      const res = await authFetch(`${API_URL}?downloadAssets=true&id=${encodeURIComponent(model.modelId)}`, { signal });
      if (signal.aborted) return;
      if (!res.ok) throw new Error(res.status === 404 ? 'empty' : 'list');
      const { assets } = await res.json();
      if (signal.aborted) return;
      if (!assets || assets.length === 0) throw new Error('empty');

      safeSet(() => setProgress({ done: 0, total: assets.length }));
      const zip = new JSZip();
      const folder = zip.folder(safeName);
      let done = 0;
      const failed = [];
      const tasks = assets.map((a) => async () => {
        if (signal.aborted) return;
        try {
          folder.file(a.name, await fetchAsset(a.url, { signal, onBytes: bumpBytes }));
        } catch {
          if (signal.aborted) return;
          failed.push(a.name);
        } finally {
          done += 1;
          safeSet(() => setProgress({ done, total: assets.length }));
        }
      });
      await runPool(tasks, 3, signal);
      if (signal.aborted) return;
      safeSet(() => setDownloaded(bytesRef.current));

      if (failed.length === assets.length) throw new Error('all-failed');

      // STORE (no deflate) — splat/sog/ply/mov are already large and barely
      // compressible; deflating them only burns memory and time.
      safeSet(() => setBuilding(true));
      const content = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
      if (signal.aborted) return;
      saveAs(content, `${safeName}_assets.zip`);

      if (failed.length > 0) {
        // Partial archive — surface it rather than closing silently.
        safeSet(() => {
          setBuilding(false);
          setError(`${failed.length} of ${assets.length} file(s) could not be fetched and were left out of the archive.`);
          setWorking(false);
        });
        return;
      }
      onClose();
    } catch (e) {
      if (signal.aborted) return;
      safeSet(() => {
        setBuilding(false);
        setError(
          e && e.message === 'empty'
            ? 'No downloadable assets found for this model.'
            : 'Download failed. Please try again.'
        );
        setWorking(false);
      });
    }
  };

  // Cancel is always live: mid-download it aborts every in-flight fetch and
  // the retry backoff, then dismisses; otherwise it just dismisses.
  const handleCancel = () => {
    if (abortRef.current) abortRef.current.abort();
    onClose();
  };

  let status;
  if (building) {
    status = `Building the ZIP archive… (${fmtMB(downloaded)})`;
  } else if (working && progress.total) {
    status = `Downloading ${progress.done} / ${progress.total} files… (${fmtMB(downloaded)})`;
  } else if (working) {
    status = 'Preparing download…';
  } else {
    status = `All assets for “${label}” will be downloaded as a single ZIP archive.`;
  }

  return (
    <ModalOverlay onClick={(e) => e.stopPropagation()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={modalCard}>
        <p style={modalTitle}>Download all assets</p>
        <p style={modalBody}>{status}</p>

        {error && (
          <p style={{ color: '#ef4444', fontSize: 13, margin: '0 0 14px', fontFamily: 'Inter, Arial, sans-serif' }}>{error}</p>
        )}

        <div style={MODAL_BTN_ROW}>
          <button type="button" onClick={handleCancel} style={{ ...modalSecondaryBtn, flex: 1 }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={working}
            style={{ ...modalPrimaryBtn, flex: 1, cursor: working ? 'default' : 'pointer', opacity: working ? 0.7 : 1 }}
          >
            {working ? 'Downloading…' : error ? 'Retry' : 'Yes'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default DownloadModal;
